import {
    useMutation,
    UseMutationOptions,
    useQuery
} from '@tanstack/react-query';
import {
    LibrarySkillsAPI,
    TSkillData,
    TSkillParameterData
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';
import type { MutationOptions } from '@/types/common';

export const librarySkillsAPI = new LibrarySkillsAPI(axios);

// GET library skills
export const useLibrarySkillsQuery = (libraryId?: string) => {
    return useQuery({
        queryKey: ['library/skills', libraryId],
        queryFn: async () => {
            if (!libraryId) {
                throw new Error('Library ID is required');
            }
            return librarySkillsAPI.getLibrarySkills(libraryId);
        }
    });
};

// CREATE library skill
export const useCreateLibrarySkillMutation = (
    options?: UseMutationOptions<
        { id: string },
        Error,
        { libraryId: string; data: TSkillData }
    >
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            libraryId,
            data
        }: {
            libraryId: string;
            data: TSkillData;
        }) => {
            return librarySkillsAPI.createLibrarySkill(libraryId, data);
        }
    });
};

// UPDATE library skill
export const useUpdateLibrarySkillMutation = (
    options?: MutationOptions<{
        libraryId: string;
        skillId: string;
        data: TSkillData;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            libraryId,
            skillId,
            data
        }: {
            libraryId: string;
            skillId: string;
            data: TSkillData;
        }) => {
            return librarySkillsAPI.updateLibrarySkill(
                libraryId,
                skillId,
                data
            );
        }
    });
};

// DELETE library skill
export const useDeleteLibrarySkillMutation = (
    options?: MutationOptions<{ libraryId: string; skillId: string }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            libraryId,
            skillId
        }: {
            libraryId: string;
            skillId: string;
        }) => {
            return librarySkillsAPI.deleteLibrarySkill(libraryId, skillId);
        }
    });
};

// CREATE library skill parameter
export const useCreateLibrarySkillParameterMutation = (
    options?: MutationOptions<{
        libraryId: string;
        skillId: string;
        data: TSkillParameterData;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            libraryId,
            skillId,
            data
        }: {
            libraryId: string;
            skillId: string;
            data: TSkillParameterData;
        }) => {
            return librarySkillsAPI.createLibrarySkillParameter(
                libraryId,
                skillId,
                data
            );
        }
    });
};

// DELETE library skill parameter
export const useDeleteLibrarySkillParameterMutation = (
    options?: MutationOptions<{
        libraryId: string;
        skillId: string;
        parameterId: string;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            libraryId,
            skillId,
            parameterId
        }: {
            libraryId: string;
            skillId: string;
            parameterId: string;
        }) => {
            return librarySkillsAPI.deleteLibrarySkillParameter(
                libraryId,
                skillId,
                parameterId
            );
        }
    });
};

// UPDATE library skill parameter
export const useUpdateLibrarySkillParameterMutation = (
    options?: MutationOptions<{
        libraryId: string;
        skillId: string;
        parameterId: string;
        data: TSkillParameterData;
    }>
) => {
    return useMutation({
        ...options,
        mutationFn: async ({
            libraryId,
            skillId,
            parameterId,
            data
        }: {
            libraryId: string;
            skillId: string;
            parameterId: string;
            data: TSkillParameterData;
        }) => {
            return librarySkillsAPI.updateLibrarySkillParameter(
                libraryId,
                skillId,
                parameterId,
                data
            );
        }
    });
};
